import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiUser,
  FiMail,
  FiPhone,
  FiUpload,
  FiCheck,
  FiChevronRight,
  FiChevronLeft,
  FiAlertCircle,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { admissionAPI } from "../services/api";
import { useSiteSettings } from "../context/SiteSettingsContext";

const STEPS = [
  "Student Info",
  "Parent Info",
  "Review & Submit",
];

const initialForm = {
  // Step 1
  studentName: "",
  dateOfBirth: "",
  gender: "",
  classApplyingFor: "",
  previousSchool: "",
  nationality: "Nepali",
  // Step 2
  parentName: "",
  relationship: "",
  email: "",
  phone: "",
  alternatePhone: "",
  address: "",
  city: "",
  occupation: "",
  // Step 3
  documents: null,
  notes: "",
};

const classOptions = [
  "Nursery",
  "KG",
  "Grade 1",
  "Grade 2",
  "Grade 3",
  "Grade 4",
  "Grade 5",
  "Grade 6",
  "Grade 7",
  "Grade 8",
  "Grade 9",
  "Grade 10",
  "Grade 11",
  "Grade 12",
];

const SectionLabel = ({ text }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-8 h-1 rounded-full bg-[#2563EB]" />
    <span className="text-[#2563EB] text-xs font-bold uppercase tracking-[0.25em]">{text}</span>
  </div>
);

const AdmissionForm = () => {
  const { settings } = useSiteSettings();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [applicationId, setApplicationId] = useState("");

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setForm((prev) => ({ ...prev, [name]: files ? files[0] : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateStep = () => {
    const newErrors = {};
    if (step === 0) {
      if (!form.studentName.trim())
        newErrors.studentName = "Required";
      if (!form.dateOfBirth)
        newErrors.dateOfBirth = "Required";
      if (!form.gender) newErrors.gender = "Required";
      if (!form.classApplyingFor)
        newErrors.classApplyingFor = "Required";
    } else if (step === 1) {
      if (!form.parentName.trim())
        newErrors.parentName = "Required";
      if (!form.email.trim()) newErrors.email = "Required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
        newErrors.email = "Invalid email";
      if (!form.phone.trim()) newErrors.phone = "Required";
      if (!form.address.trim()) newErrors.address = "Required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep()) setStep((s) => s + 1);
  };

  const prevStep = () => setStep((s) => s - 1);

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, val]) => {
        if (val !== null && val !== undefined) formData.append(key, val);
      });
      const response = await admissionAPI.create(formData);
      const id =
        response.data?.data?.admission?._id ||
        `GA-${Date.now().toString().slice(-6)}`;
      setApplicationId(id);
      setSubmitted(true);
      toast.success("Application submitted successfully!");
    } catch (error) {
      const msg =
        error.response?.data?.message ||
        "Failed to submit application. Please try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const ErrorMsg = ({ name }) =>
    errors[name] ? (
      <div className="flex items-center gap-1 mt-2 text-red-500 text-xs font-mono">
        <FiAlertCircle className="w-3 h-3" />
        {errors[name]}
      </div>
    ) : null;


  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F0F4FF] flex items-center justify-center px-4 py-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="bg-white rounded-2xl p-10 max-w-lg w-full text-center border border-blue-100 shadow-lg"
        >
          <div className="w-20 h-20 bg-[#2563EB] rounded-2xl flex items-center justify-center mx-auto mb-8 shadow-md">
            <FiCheck className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl font-bold text-[#0F172A] mb-4">
            Application <span className="text-[#2563EB]">Submitted!</span>
          </h2>
          <p className="text-slate-500 text-sm leading-relaxed mb-8">
            Thank you for applying to {settings?.schoolName || "Hillbert English Boarding School "}. We have received your application and will contact you within 3-5 business days.
          </p>
          <div className="bg-[#1e3a8a] rounded-xl p-6 mb-8">
            <p className="text-sky-300 text-[10px] font-bold uppercase tracking-widest mb-1">Application Reference ID</p>
            <p className="text-white text-2xl font-bold">{applicationId}</p>
          </div>
          <p className="text-slate-400 text-xs mb-8 border-t border-blue-50 pt-6">
            Please save this reference ID. You will need it to track your application status.
          </p>
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-4 bg-[#2563EB] text-white text-xs font-bold rounded-xl uppercase tracking-widest hover:bg-[#1D4ED8] transition-colors shadow-sm"
          >
            Back to Home
          </a>
        </motion.div>
      </div>
    );
  }

  const inputClass = (name) =>
    `w-full px-5 py-4 bg-[#F0F4FF] border border-blue-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB] text-slate-900 font-medium placeholder-slate-400 text-sm transition-all ${errors[name] ? "ring-2 ring-red-400 border-red-300 bg-red-50" : ""}`;
  const selectClass = (name) =>
    `w-full px-5 py-4 bg-[#F0F4FF] border border-blue-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB] text-slate-900 font-medium text-sm transition-all appearance-none ${errors[name] ? "ring-2 ring-red-400 border-red-300 bg-red-50" : ""}`;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-[#1e3a8a] py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10"
          style={{ backgroundImage: "radial-gradient(circle, #ffffff 1px, transparent 1px)", backgroundSize: "32px 32px" }} />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#2563EB]" />
        <div className="relative max-w-3xl mx-auto px-8 sm:px-12 text-center">
          <div className="flex items-center justify-center gap-3 mb-5">
            <div className="w-8 h-1 rounded-full bg-sky-400" />
            <p className="text-sky-400 text-xs font-bold uppercase tracking-[0.25em]">Join Us Today</p>
            <div className="w-8 h-1 rounded-full bg-sky-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white leading-tight mb-4">
            Online <span className="text-sky-300">Admission</span>
          </h1>
          <p className="text-blue-200/70 text-sm">
            Academic Year 2025-2026 | {settings?.schoolName || "Hillbert English Boarding School "}
          </p>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-8 sm:px-12 py-16 lg:py-20">
        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            {STEPS.map((label, idx) => (
              <div key={idx} className="flex items-center flex-1">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-xs font-bold transition-all shrink-0 shadow-sm ${
                  idx < step
                    ? "bg-[#1e3a8a] text-sky-300"
                    : idx === step
                      ? "bg-[#2563EB] text-white"
                      : "bg-[#F0F4FF] text-slate-400 border border-blue-100"
                }`}>
                  {idx < step ? <FiCheck className="w-5 h-5" /> : `0${idx + 1}`}
                </div>
                {idx < STEPS.length - 1 && (
                  <div className={`flex-1 h-1 mx-3 rounded-full ${idx < step ? "bg-[#2563EB]" : "bg-blue-100"}`} />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between">
            {STEPS.map((label, idx) => (
              <span
                key={idx}
                className={`text-[10px] font-bold uppercase tracking-widest ${idx === step ? "text-[#2563EB]" : "text-slate-400"}`}
                style={{ width: "44px", textAlign: "center" }}
              >
                {label}
              </span>
            ))}
          </div>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl p-8 lg:p-12 border border-blue-100 shadow-sm relative">
          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div
                key="step0"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <SectionLabel text="Step 01" />
                <h2 className="text-3xl font-bold text-[#0F172A] mb-8">
                  Student <span className="text-[#2563EB]">Information</span>
                </h2>
                
                <div className="space-y-6">
                  <div>
                    <input
                      name="studentName"
                      value={form.studentName}
                      onChange={handleChange}
                      placeholder="Student's Full Name *"
                      className={inputClass("studentName")}
                    />
                    <ErrorMsg name="studentName" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <input
                        type="date"
                        name="dateOfBirth"
                        value={form.dateOfBirth}
                        onChange={handleChange}
                        className={inputClass("dateOfBirth")}
                      />
                      <ErrorMsg name="dateOfBirth" />
                    </div>
                    <div>
                      <select
                        name="gender"
                        value={form.gender}
                        onChange={handleChange}
                        className={selectClass("gender")}
                      >
                        <option value="">Select Gender *</option>
                        <option>Male</option>
                        <option>Female</option>
                        <option>Other</option>
                      </select>
                      <ErrorMsg name="gender" />
                    </div>
                  </div>
                  <div>
                    <select
                      name="classApplyingFor"
                      value={form.classApplyingFor}
                      onChange={handleChange}
                      className={selectClass("classApplyingFor")}
                    >
                      <option value="">Class Applying For *</option>
                      {classOptions.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                    <ErrorMsg name="classApplyingFor" />
                  </div>
                  <div>
                    <input
                      name="previousSchool"
                      value={form.previousSchool}
                      onChange={handleChange}
                      placeholder="Previous School (if any)"
                      className={inputClass("previousSchool")}
                    />
                  </div>
                  <div>
                    <input
                      name="nationality"
                      value={form.nationality}
                      onChange={handleChange}
                      placeholder="Nationality"
                      className={inputClass("nationality")}
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <SectionLabel text="Step 02" />
                <h2 className="text-3xl font-bold text-[#0F172A] mb-8">
                  Parent <span className="text-[#2563EB]">Information</span>
                </h2>
                
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <input
                        name="parentName"
                        value={form.parentName}
                        onChange={handleChange}
                        placeholder="Parent's Full Name *"
                        className={inputClass("parentName")}
                      />
                      <ErrorMsg name="parentName" />
                    </div>
                    <div>
                      <select
                        name="relationship"
                        value={form.relationship}
                        onChange={handleChange}
                        className={selectClass("relationship")}
                      >
                        <option value="">Relationship</option>
                        <option>Father</option>
                        <option>Mother</option>
                        <option>Guardian</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <div className="relative">
                      <FiMail className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                      <input
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="Email Address *"
                        className={`${inputClass("email")} pl-12`}
                      />
                    </div>
                    <ErrorMsg name="email" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <div className="relative">
                        <FiPhone className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                        <input
                          name="phone"
                          value={form.phone}
                          onChange={handleChange}
                          placeholder="Phone Number *"
                          className={`${inputClass("phone")} pl-12`}
                        />
                      </div>
                      <ErrorMsg name="phone" />
                    </div>
                    <div>
                      <input
                        name="alternatePhone"
                        value={form.alternatePhone}
                        onChange={handleChange}
                        placeholder="Alternate Phone"
                        className={inputClass("alternatePhone")}
                      />
                    </div>
                  </div>
                  <div>
                    <textarea
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      rows={2}
                      placeholder="Home Address *"
                      className={`${inputClass("address")} resize-none`}
                    />
                    <ErrorMsg name="address" />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <input
                        name="city"
                        value={form.city}
                        onChange={handleChange}
                        placeholder="City"
                        className={inputClass("city")}
                      />
                    </div>
                    <div>
                      <input
                        name="occupation"
                        value={form.occupation}
                        onChange={handleChange}
                        placeholder="Occupation"
                        className={inputClass("occupation")}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <SectionLabel text="Step 03" />
                <h2 className="text-3xl font-bold text-[#0F172A] mb-8">
                  Review &amp; <span className="text-[#2563EB]">Submit</span>
                </h2>

                {/* Review summary */}
                <div className="bg-[#F0F4FF] rounded-xl border border-blue-100 p-6 mb-8">
                  <h3 className="font-bold text-[#0F172A] text-sm uppercase tracking-widest mb-4">
                    Application Summary
                  </h3>
                  <div className="grid grid-cols-2 gap-y-4 text-sm">
                    <span className="text-slate-400 font-mono">Student Name</span>
                    <span className="font-bold text-slate-950">
                      {form.studentName || "-"}
                    </span>
                    <span className="text-slate-400 font-mono">Class Applying</span>
                    <span className="font-bold text-slate-950">
                      {form.classApplyingFor || "-"}
                    </span>
                    <span className="text-slate-400 font-mono">Date of Birth</span>
                    <span className="font-bold text-slate-950">
                      {form.dateOfBirth || "-"}
                    </span>
                    <span className="text-slate-400 font-mono">Parent Name</span>
                    <span className="font-bold text-slate-950">
                      {form.parentName || "-"}
                    </span>
                    <span className="text-slate-400 font-mono">Contact Email</span>
                    <span className="font-bold text-slate-950">
                      {form.email || "-"}
                    </span>
                    <span className="text-slate-400 font-mono">Phone</span>
                    <span className="font-bold text-slate-950">
                      {form.phone || "-"}
                    </span>
                  </div>
                </div>

                {/* Document Upload */}
                <div className="mb-8">
                  <label className="flex flex-col items-center justify-center w-full h-40 bg-[#F0F4FF] border-2 border-dashed border-blue-200 rounded-xl cursor-pointer hover:border-[#2563EB] hover:bg-blue-50 transition-colors">
                    <FiUpload className="w-8 h-8 text-slate-400 mb-3" />
                    <span className="text-slate-950 font-bold text-sm mb-1">
                      {form.documents
                        ? form.documents.name
                        : "Upload Documents (PDF/ZIP)"}
                    </span>
                    <span className="text-slate-400 text-xs font-mono">
                      Max file size: 10MB
                    </span>
                    <input
                      type="file"
                      name="documents"
                      onChange={handleChange}
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png,.zip"
                    />
                  </label>
                </div>

                <div>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={3}
                    placeholder="Additional notes or questions..."
                    className={`${inputClass("notes")} resize-none`}
                  />
                </div>

                <div className="mt-8 pt-6 border-t border-blue-50">
                  <p className="text-xs text-slate-500 leading-relaxed">
                    By submitting this form, you confirm that all information
                    provided is accurate and complete. Providing false information
                    will result in cancellation of admission.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between mt-10">
            <button
              onClick={prevStep}
              disabled={step === 0}
              className="flex items-center gap-3 px-6 py-4 bg-[#F0F4FF] text-slate-500 text-xs font-bold rounded-xl uppercase tracking-widest border border-blue-100 hover:bg-blue-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <FiChevronLeft className="w-4 h-4" />
              Previous
            </button>

            {step < 2 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-3 px-6 py-4 bg-[#2563EB] text-white text-xs font-bold rounded-xl uppercase tracking-widest hover:bg-[#1D4ED8] transition-colors shadow-sm"
              >
                Next Step
                <FiChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-3 px-6 py-4 bg-[#2563EB] text-white text-xs font-bold rounded-xl uppercase tracking-widest hover:bg-[#1D4ED8] transition-colors disabled:opacity-70 shadow-sm"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit
                    <FiCheck className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdmissionForm;
